import { Box } from "@chakra-ui/react";
import React from "react";

import { faEnvelope, faLink } from "@fortawesome/free-solid-svg-icons";

import {
  faFacebookF,
  faFacebookMessenger,
  faGetPocket,
  faLinkedin,
  faPinterestP,
  faTwitter,
} from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  EmailShareButton,
  FacebookMessengerShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  PinterestShareButton,
  PocketShareButton,
  TwitterShareButton
} from "react-share";

import { LinkIcon } from "@chakra-ui/icons";
import { fixImagePath } from "@src/utils/dom";
import {
  ShareButtonFieldsFragment
} from "./__generated/ShareButtons.generated";

export interface IRenderButton {
  itm: ShareButtonFieldsFragment;
  i: number;
  buttonStyle: any;
  size: number;
  pageUrl: string;
  pageImage?: string;
  title?: string;
  fontColor: string;
  description?: string;
  shareType?: string;
  appId?: string;
}

export const getBackgroundColor = (
  theme?: string,
  buttonStyle?: string
): string => {
  if (!theme || theme === "default") {
    switch (buttonStyle) {
      case "pocket":
        return "#EE3F55";
      case "facebook":
        return "#425893";
      case "facebook messenger":
        return "#4994EC";
      case "pinterest":
        return "#D32D2F";
      case "linkedin":
        return "#2D64BC";
      case "twitter":
        return "#4DA9E7";
      default:
        return "#7F7F7F";
    }
  } else if (theme === "light") {
    return "brandPrimary.500";
  }
  return "#FFFFFF";
};

export interface IGetButtonProps {
  itm: ShareButtonFieldsFragment;
  i: number;
  description?: string;
  images?: any[];
  theme?: string;
  fontColor: string;
  pageUrl: string;
  size: number;
  title?: string;
}

export const getButton = ({
  itm,
  i,
  description,
  theme,
  fontColor,
  pageUrl,
  size,
  title,
  images,
}: IGetButtonProps): React.ReactNode => {
  const buttonStyle: any = {
    padding: "0.65rem",
    borderRadius: 55,
    width: size,
    height: size,
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
    display: "flex",
  };
  const shareType = itm?.shareType?.toString().toLowerCase();
  buttonStyle.backgroundColor = getBackgroundColor(theme, shareType);
  const pageImage =
    images && images.length > 0
      ? fixImagePath(images[0]?.fields?.file?.url)
      : "";
  return renderButton({
    itm,
    i,
    buttonStyle,
    shareType,
    description,
    fontColor,
    pageUrl,
    size,
    title,
    pageImage,
  });
};

interface ICopyShareButtonProps {
  url: string;
  style: any;
  key: number;
  size: number;
  fontColor: string;
  children: any;
}

const CopyShareButton = (props: ICopyShareButtonProps): React.JSX.Element => {
  const { size, fontColor } = props;
  const handleCopy = async () => {
    const url = location?.href?.toString();

    try {
      await navigator.clipboard.writeText(url);
      alert(`Copied the url: ${url}`);
    } catch (e) {
      alert(`There was an error copying this page's url`);
    }
  };
  return (
    <Box id="copyurlz" onClick={handleCopy} {...props} >
      <LinkIcon color={fontColor}/>
    </Box>
  );
};

export const renderButton = ({
  itm,
  i,
  buttonStyle,
  size,
  pageUrl,
  pageImage,
  title,
  fontColor,
  description,
  shareType,
  appId,
}: IRenderButton): React.ReactNode => {
  let button = <></>;

  switch (shareType) {
    case "pocket":
      button = (
        <>
          <Box as="span" width={size} height={size}>
            <PocketShareButton
              key={i}
              data-testid={`sharebutton-${i}`}
              url={pageUrl}
              title={title}
              style={buttonStyle}
            >
              <FontAwesomeIcon
                width={size}
                height={size}
                icon={faGetPocket}
                color={fontColor}
              />
            </PocketShareButton>
          </Box>
        </>
      );
      break;
    case "facebook messenger":
      button = (
        <>
          <Box as="span" width={size} height={size}>
            <FacebookMessengerShareButton
              key={i}
              data-testid={`sharebutton-${i}`}
              url={pageUrl}
              appId={itm?.appId || ""}
              style={buttonStyle}
            >
              <FontAwesomeIcon
                width={size}
                height={size}
                icon={faFacebookMessenger}
                color={fontColor}
              />
            </FacebookMessengerShareButton>
          </Box>
        </>
      );
      break;
    case "facebook":
      button = (
        <>
          <Box as="span" width={size} height={size}>
            <FacebookShareButton
              key={i}
              data-testid={`sharebutton-${i}`}
              url={pageUrl}
              quote={itm?.quote || undefined}
              hashtag={itm?.hashtag || undefined}
              style={buttonStyle}
            >
              <FontAwesomeIcon
                width={size}
                height={size}
                icon={faFacebookF}
                color={fontColor}
              />
            </FacebookShareButton>
          </Box>
        </>
      );
      break;
    case "Linkedin":
      button = (
        <>
          <Box as="span" width={size} height={size}>
            <LinkedinShareButton
              key={i}
              data-testid={`sharebutton-${i}`}
              url={pageUrl}
              title={title}
              summary={description}
              source={itm?.quote || undefined}
              style={buttonStyle}
            >
              <FontAwesomeIcon
                width={size}
                height={size}
                icon={faLinkedin}
                color={fontColor}
              />
            </LinkedinShareButton>
          </Box>
        </>
      );
      break;
    case "twitter":
      button = (
        <>
          <Box as="span" width={size} height={size}>
            <TwitterShareButton
              key={i}
              data-testid={`sharebutton-${i}`}
              url={pageUrl}
              title={title}
              via={itm?.via || undefined}
              hashtags={(itm?.hashtags as string[]) || undefined}
              //via={itm?.via || undefined}
              //title={itm?.title || undefined}
              //hashtags={(itm?.hashtags as string[] | undefined) || undefined}
              style={{ ...buttonStyle, ...{ padding: "0.45rem" } }}
            >
              {/*<Image
                width={35}
                height={35}
                objectFit="contain"
                src="/icon-twitter.png"
                alt="no img"
        />*/}
              <FontAwesomeIcon
                width={buttonStyle.width - 1}
                height={buttonStyle.height - 1}
                icon={faTwitter}
                color={fontColor}
              />
              {/* <TwitterIcon
                iconFillColor={"black"}
                size={30}
                bgStyle={{ fill: "#ffffff" }}
                round={true}
        />*/}
            </TwitterShareButton>
          </Box>
        </>
      );
      break;
    case "pinterest":
      if (pageImage !== "") {
        button = (
          <>
            <Box as="span" width={size} height={size}>
              <PinterestShareButton
                key={i}
                data-testid={`sharebutton-${i}`}
                url={pageUrl}
                description={description}
                media={pageImage || ""}
                style={buttonStyle}
              >
                {/*<Image
                  width={35}
                  height={35}
                  objectFit="contain"
                  src="/icon-twitter.png"
                  alt="no img"
          />*/}
                <FontAwesomeIcon
                  width={size}
                  height={size}
                  icon={faPinterestP}
                  color={fontColor}
                />
                {/* <TwitterIcon
                  iconFillColor={"black"}
                  size={30}
                  bgStyle={{ fill: "#ffffff" }}
                  round={true}
          />*/}
              </PinterestShareButton>
            </Box>
          </>
        );
      }
      break;
    case "email":
      button = (
        <>
          <Box as="span" width={size} height={size}>
            <EmailShareButton
              key={i}
              data-testid={`sharebutton-${i}`}
              url={pageUrl}
              subject={title}
              body={
                "I found this on the Senior Healthcare Direct website, and thought you might be interested:"
              }
              separator=" "
              style={buttonStyle}
            >
              <FontAwesomeIcon
                color={fontColor}
                width={size}
                height={size}
                icon={faEnvelope}
              />
            </EmailShareButton>
          </Box>
        </>
      );
      break;
    case "copy":
      // ATTN: CAN'T GET THIS TO WORK
      button = (
        <>
          <Box as="span" width={size} height={size}>
            <CopyShareButton
              key={i}
              data-testid={`sharebutton-${i}`}
              url={pageUrl}
              style={{ ...{ cursor: "pointer" }, ...buttonStyle }}
              fontColor={fontColor}
              size={size}
            >
              <FontAwesomeIcon
                color={fontColor}
                width={size}
                height={size}
                icon={faLink}
              />
            </CopyShareButton>
          </Box>
        </>
      );
      break;
    default:
      button = <></>;
      break;
  }

  return button;
};
